// Coworker face-photo card (Access Provider Phase 2 Task 10/14, revised
// Phase 3 Task 10). Mounted from a row action on CoworkersPage. Follows the
// access-readers-section idioms (loading/error/empty/data, toast on
// mutation, ConfirmDialog for delete) scoped to a single coworker's photo
// rather than a full section.
//
// The Phase-2 stopgap upload control was removed: face photos are now
// (re)captured exclusively via the mobile self-enrollment wizard (Phase 3).
// This card instead surfaces a "copy invite link" affordance so an admin can
// hand the coworker's COMPANY invite URL back to them to re-run that wizard.
// The card only receives `coworkerId` as a prop (see CoworkersPage.tsx), so
// it resolves the coworker's companyId itself via the existing
// `GET /api/coworkers/:id` route before fetching the company's invite URL.
import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Banner,
  Button,
  ClipboardText,
  Empty,
  Loader,
  Text,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { TrashIcon } from '@phosphor-icons/react'
import type { Coworker } from '@hub-wifi/shared'
import { apiRequest, ApiError } from '../../api/client'
import { deleteFacePhoto, getFacePhoto } from '../../api/facePhotos'
import { getCompanyEnrollment } from '../../api/enrollment'
import { useTranslation } from '../../i18n'
import { ConfirmDialog } from '../ConfirmDialog'

export function FacePhotoCard({ coworkerId }: { coworkerId: string }) {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Tracks the current object URL outside React state so unmount/replace can
  // always revoke the right one, even across a fast re-render.
  const objectUrlRef = useRef<string | null>(null)

  const setPreview = useCallback((url: string | null) => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
    }
    objectUrlRef.current = url
    setPreviewUrl(url)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const [blob, coworker] = await Promise.all([
        getFacePhoto(coworkerId, token),
        apiRequest<Coworker>(`/api/coworkers/${coworkerId}`, { token }),
      ])
      setPreview(blob ? URL.createObjectURL(blob) : null)
      if (coworker.companyId) {
        const enrollment = await getCompanyEnrollment(coworker.companyId, token)
        setInviteUrl(enrollment.inviteUrl)
      } else {
        setInviteUrl(null)
      }
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'))
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` is stable
    // in the real i18n provider; omitted so a test double returning a fresh
    // `t` reference per render can't spin this into a refetch loop (mirrors
    // device-reconcile-panel.tsx).
  }, [coworkerId, getAccessTokenSilently, setPreview])

  useEffect(() => {
    void load()
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [load])

  async function handleDelete() {
    setDeleting(true)
    try {
      const token = await getAccessTokenSilently()
      await deleteFacePhoto(coworkerId, token)
      setDeleteOpen(false)
      toasts.add({ variant: 'success', title: t('facePhoto.deleteSuccess') })
      await load()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Text as="h3" variant="heading3">
        {t('facePhoto.title')}
      </Text>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {!loading && !error && (
        <>
          {previewUrl ? (
            <div className="flex flex-col gap-2">
              <img
                src={previewUrl}
                alt={t('facePhoto.previewAlt')}
                className="h-32 w-32 rounded object-cover"
              />
              <Button
                variant="secondary-destructive"
                size="sm"
                icon={<TrashIcon size={16} />}
                onClick={() => setDeleteOpen(true)}
              >
                {t('facePhoto.delete')}
              </Button>
            </div>
          ) : (
            <Empty title={t('facePhoto.empty')} />
          )}

          {inviteUrl && (
            <ClipboardText
              text={inviteUrl}
              labels={{ copyAction: t('facePhoto.copyInviteLink') }}
              tooltip={{ copiedText: t('enrollment.inviteLink.copySuccess') }}
            />
          )}
        </>
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('facePhoto.deleteConfirm.title')}
        description={t('facePhoto.deleteConfirm.description')}
        confirmLabel={t('facePhoto.delete')}
        variant="destructive"
        loading={deleting}
        onConfirm={handleDelete}
      />
    </div>
  )
}

FacePhotoCard.displayName = 'FacePhotoCard'
